import { Button, Grid, Link, MenuItem, TextField } from '@mui/material';
import UserSearchFormItem from 'components/profile/UserSearchFormItem';
import Modal from 'components/shared/Modal';
import { useSnackbar } from 'notistack';
import React, { useCallback, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import UserRelationshipService from 'services/api/UserRelationshipService';
import { ModalProps } from 'store/types/ComponentProps';
import SelectOption, { UserSelectOption } from 'store/types/SelectOption';
import { LinkUserRelationshipData } from 'store/types/UserRelationship';
import { defaultFormProps, getRequiredValidationRule, getValidationProps } from 'util/Form';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';

import styles from './LinkUserRelationshipModal.module.scss';

interface LinkUserRelationshipModalProps extends ModalProps {
  types: SelectOption[];
  refetch: () => void;
  onAddPersonModalOpen: () => void;
  customerId?: string;
}

interface LinkUserRelationshipFormValues
  extends Omit<LinkUserRelationshipData, 'relatedCustomerId' | 'email' | 'startDate'> {
  users: UserSelectOption[];
}

const defaultValues: LinkUserRelationshipFormValues = {
  users: [],
  typeId: '',
};

const LinkUserRelationshipModal: React.FunctionComponent<LinkUserRelationshipModalProps> = ({
  types,
  open,
  onClose,
  refetch,
  onAddPersonModalOpen,
  customerId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const form = useForm<LinkUserRelationshipFormValues>({ ...defaultFormProps, defaultValues });
  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    reset,
  } = form;

  const handleModalClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const handleFormSubmit = useCallback(
    (formValues: LinkUserRelationshipFormValues) => {
      setLoading(true);

      const data: LinkUserRelationshipData = {
        relatedCustomerId: formValues.users[0].id,
        email: formValues.users[0].email,
        typeId: formValues.typeId,
        customerId,
      };

      UserRelationshipService.linkRelationshipUser(data)
        .then(() => {
          enqueueSnackbar('User successfully added', { variant: 'success' });
          setLoading(false);
          handleModalClose();
          refetch();
        })
        .catch((errorMessage) => {
          enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
          setLoading(false);
        });
    },
    [customerId, enqueueSnackbar, handleModalClose, refetch]
  );

  return (
    <FormProvider {...form}>
      <Modal
        loading={loading}
        title={'Search For Contact'}
        open={open}
        maxWidth={'md'}
        actions={
          <>
            <Button color={'secondary'} variant={'outlined'} onClick={handleModalClose}>
              {'Cancel'}
            </Button>
            <Button
              type={'submit'}
              variant={'contained'}
              onClick={handleSubmit(handleFormSubmit)}
              disabled={loading || !isValid}
            >
              {'Save'}
            </Button>
          </>
        }
      >
        <Grid {...defaultGridContainerProps} spacing={2}>
          <Grid {...defaultGridItemProps}>
            <UserSearchFormItem />
          </Grid>
          <Grid {...defaultGridItemProps}>
            <span className={styles.helperText}>{'Friend not found? Click '}</span>
            <Link className={styles.link} onClick={onAddPersonModalOpen}>
              {'here'}
            </Link>
          </Grid>
          <Grid {...defaultGridItemProps} md={6}>
            <Controller
              render={({ field }) => (
                <TextField
                  {...field}
                  {...getValidationProps('typeId', errors)}
                  select={true}
                  required={true}
                  label={'Select Relationship Type'}
                >
                  {types.map(({ name, id }) => (
                    <MenuItem key={id} value={id}>
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              rules={{ required: getRequiredValidationRule('type', true, true) }}
              name={'typeId'}
              control={control}
            />
          </Grid>
        </Grid>
      </Modal>
    </FormProvider>
  );
};

export default LinkUserRelationshipModal;
